<template>
  <div class="tw-p-6">Redirecting…</div>
</template>

<script setup lang="ts">
import { onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthenticationStore } from "@/store/authentication.store";
import { usePageMetaStore } from "@/store/pageMeta.store";

import { navigateToMspOrganisation } from "@/utils/navigation/organisationNavigation";
import { $http } from "@/plugins/api";
import { TIER } from "@/constants/tier";
import { getApiClientUrl } from "@/config/runtimeConfig";

const route = useRoute();
const router = useRouter();
const authStore = useAuthenticationStore();
const pageMetaStore = usePageMetaStore();

const organisationUuid = route.params.organisationUuid as string;
const suffix =
  route.params.rest && route.params.rest !== "" ? `/${route.params.rest}` : "";
const targetQuery = route.query;
const targetHash = route.hash;

const buildScopedPath = (orgType: string, orgUuid: string) =>
  `/${orgType}/${orgUuid}${suffix}`;

const pushScoped = async (orgType: string, orgUuid: string) => {
  await router.push({
    path: buildScopedPath(orgType, orgUuid),
    query: targetQuery,
    hash: targetHash,
  });
};

const getRootOrgUuid = () =>
  authStore.selectedAccount?.uuid ||
  authStore.authUser?.organisation_uuid ||
  authStore.authUser?.organisationUuid;

const goToCurrentCustomer = async () => {
  await authStore.setCustomer({
    uuid: organisationUuid,
    name: authStore.selectedAccount?.name || "",
  });
  await pushScoped("customer", organisationUuid);
};

const goToCurrentMsp = async () => {
  await navigateToMspOrganisation(
    { router },
    {
      id: organisationUuid,
      name: authStore.selectedAccount?.name || "",
    },
  );
  if (suffix) {
    await pushScoped("msp", organisationUuid);
  }
};

const goToCustomer = async (org: {
  uuid: string;
  name: string;
  parent_uuid?: string;
  parent_name?: string;
}) => {
  const rootOrgUuid = getRootOrgUuid();

  if (org.parent_uuid && org.parent_uuid !== rootOrgUuid) {
    await authStore.setMspUser({
      uuid: org.parent_uuid,
      name: org.parent_name || "",
    });
    pageMetaStore.setMSP(org.parent_name || "");
  } else {
    await authStore.setMspUser(null);
    pageMetaStore.setMSP("");
  }

  await authStore.setCustomer({
    uuid: org.uuid,
    name: org.name,
  });

  await pushScoped("customer", org.uuid);
};

const goToMsp = async (org: { uuid: string; name: string }) => {
  await navigateToMspOrganisation({ router }, { id: org.uuid, name: org.name });
  if (suffix) {
    await pushScoped("msp", org.uuid);
  }
};

const goToSystem = async () => {
  const rootOrgUuid = getRootOrgUuid() || organisationUuid;
  await pushScoped("system", rootOrgUuid);
};

const goToSubSystem = async (org: { uuid: string; name: string }) => {
  await authStore.setCustomer(null);
  await pushScoped("system", org.uuid);
  await authStore.setMspUser({
    uuid: org.uuid,
    name: org.name || "",
  });
  pageMetaStore.setMSP(org.name || "");
  pageMetaStore.setPageTitle({ title: org.name || "" });
};

const goToRoleDefault = async () => {
  const accountType = authStore.selectedAccount?.type_name;

  if (accountType === TIER.msps) {
    await goToCurrentMsp();
    return;
  }
  if (accountType === TIER.customer) {
    await goToCurrentCustomer();
    return;
  }

  await goToSystem();
};

onMounted(async () => {
  const rootOrgUuid = getRootOrgUuid();
  const accountType = authStore.selectedAccount?.type_name;

  if (rootOrgUuid && organisationUuid === rootOrgUuid) {
    if (accountType === TIER.msps) {
      await goToCurrentMsp();
      return;
    }
    if (accountType === TIER.customer) {
      await goToCurrentCustomer();
      return;
    }
    await goToSystem();
    return;
  }

  try {
    const response = await $http.get(
      `${getApiClientUrl()}/v1/idp/orgs/${organisationUuid}`,
    );

    const org = response.data;
    const type = (org.organisation_type || "").toLowerCase();

    if (type === TIER.msps.toLowerCase()) {
      await goToMsp(org);
      return;
    }

    if (type === TIER.customer.toLowerCase()) {
      await goToCustomer(org);
      return;
    }

    await goToSubSystem(org);
  } catch (_err) {
    // On fetch failure (e.g. 404/403), fall back to role default routing.
    await goToRoleDefault();
  }
});
</script>
